import React, { useEffect, useState, useCallback } from 'react';
import {
  getFixedAssets,
  createFixedAsset,
  disposeFixedAsset,
  getLoans,
  createLoan,
  getLoanSchedule,
  recordEmiPayment,
  getBankExposure,
  calculateDepreciation,
  postDepreciation,
  getDepreciationSchedule,
  getCompanies
} from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Truck,
  Building2,
  Landmark,
  Plus,
  Calculator,
  Calendar,
  DollarSign,
  TrendingDown,
  CreditCard,
  Eye,
  CheckCircle2,
  AlertCircle,
  Wrench,
  FileText,
  RefreshCw,
  ChevronRight,
  Clock,
  PiggyBank
} from 'lucide-react';
import { toast } from 'sonner';

// Status colors
const assetStatusColors = {
  active: 'bg-green-100 text-green-800',
  disposed: 'bg-gray-100 text-gray-800',
  idle: 'bg-amber-100 text-amber-800',
  scrapped: 'bg-red-100 text-red-800'
};

const loanStatusColors = {
  active: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  defaulted: 'bg-red-100 text-red-800'
};

const ownershipColors = {
  owned: 'bg-blue-100 text-blue-800',
  financed: 'bg-purple-100 text-purple-800',
  leased: 'bg-amber-100 text-amber-800'
};

export const AssetManagementPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('fixed-assets');
  const [loading, setLoading] = useState(false);

  // Fixed Assets state
  const [assets, setAssets] = useState([]);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assetDetailOpen, setAssetDetailOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [newAsset, setNewAsset] = useState({
    asset_name: '',
    asset_type: 'vehicle',
    ownership_type: 'owned',
    acquisition_date: new Date().toISOString().split('T')[0],
    acquisition_cost: 0,
    useful_life_months: 60,
    residual_value: 0,
    depreciation_method: 'straight_line'
  });

  // Loans state
  const [loans, setLoans] = useState([]);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [loanScheduleOpen, setLoanScheduleOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanSchedule, setLoanSchedule] = useState([]);
  const [newLoan, setNewLoan] = useState({
    loan_name: '',
    loan_type: 'vehicle',
    bank_name: '',
    principal_amount: 0,
    interest_rate: 0,
    tenure_months: 48,
    emi_amount: 0,
    disbursement_date: new Date().toISOString().split('T')[0]
  });

  // Depreciation state
  const [depreciationPeriod, setDepreciationPeriod] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [depreciationResult, setDepreciationResult] = useState(null);
  const [assetDepreciationSchedule, setAssetDepreciationSchedule] = useState(null);

  // Bank exposure state
  const [bankExposure, setBankExposure] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadAssets();
      loadLoans();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadAssets = async () => {
    setLoading(true);
    try {
      const response = await getFixedAssets(selectedCompany);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLoans = async () => {
    try {
      const response = await getLoans(selectedCompany);
      setLoans(response.data);
    } catch (error) {
      console.error('Failed to load loans:', error);
    }
  };

  const handleCreateAsset = async () => {
    try {
      await createFixedAsset({
        company_id: selectedCompany,
        ...newAsset
      });
      toast.success('Fixed asset registered successfully!');
      setAssetDialogOpen(false);
      loadAssets();
      setNewAsset({
        asset_name: '',
        asset_type: 'vehicle',
        ownership_type: 'owned',
        acquisition_date: new Date().toISOString().split('T')[0],
        acquisition_cost: 0,
        useful_life_months: 60,
        residual_value: 0,
        depreciation_method: 'straight_line'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create asset');
    }
  };

  const handleDisposeAsset = async (assetId, disposalData) => {
    try {
      await disposeFixedAsset(assetId, disposalData);
      toast.success('Asset disposed successfully');
      loadAssets();
      setAssetDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispose asset');
    }
  };

  const handleCreateLoan = async () => {
    try {
      await createLoan({
        company_id: selectedCompany,
        ...newLoan
      });
      toast.success('Loan created with EMI schedule!');
      setLoanDialogOpen(false);
      loadLoans();
      setNewLoan({
        loan_name: '',
        loan_type: 'vehicle',
        bank_name: '',
        principal_amount: 0,
        interest_rate: 0,
        tenure_months: 48,
        emi_amount: 0,
        disbursement_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    }
  };

  const handleViewLoanSchedule = async (loan) => {
    try {
      const response = await getLoanSchedule(loan.id);
      setSelectedLoan(response.data.loan);
      setLoanSchedule(response.data.schedule);
      setLoanScheduleOpen(true);
    } catch (error) {
      toast.error('Failed to load EMI schedule');
    }
  };

  const handleRecordPayment = async (loanId, emiNumber) => {
    try {
      await recordEmiPayment(loanId, {
        emi_number: emiNumber,
        payment_date: new Date().toISOString().split('T')[0]
      });
      toast.success('EMI payment recorded');
      // Refresh schedule
      const response = await getLoanSchedule(loanId);
      setLoanSchedule(response.data.schedule);
      loadLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleCalculateDepreciation = async () => {
    try {
      const response = await calculateDepreciation({
        company_id: selectedCompany,
        period_date: depreciationPeriod
      });
      setDepreciationResult(response.data);
      toast.success(`Depreciation calculated for ${response.data.entries?.length || 0} assets`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate depreciation');
    }
  };

  const handlePostDepreciation = async () => {
    try {
      const response = await postDepreciation({
        company_id: selectedCompany,
        period_date: depreciationPeriod
      });
      toast.success(`Depreciation posted: ${response.data.journal_number}`);
      setDepreciationResult(null);
      loadAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post depreciation');
    }
  };

  const handleViewAssetDepreciation = async (asset) => {
    try {
      const response = await getDepreciationSchedule(asset.id);
      setAssetDepreciationSchedule(response.data);
      setSelectedAsset(asset);
      setAssetDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load depreciation schedule');
    }
  };

  const loadBankExposure = async () => {
    try {
      const response = await getBankExposure(selectedCompany);
      setBankExposure(response.data);
    } catch (error) {
      toast.error('Failed to load bank exposure report');
    }
  };

  // Calculate EMI helper
  const calculateEMI = () => {
    const P = newLoan.principal_amount;
    const r = newLoan.interest_rate / 100 / 12;
    const n = newLoan.tenure_months;
    
    if (P > 0 && r > 0 && n > 0) {
      const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      setNewLoan(prev => ({ ...prev, emi_amount: Math.round(emi * 100) / 100 }));
    }
  };

  // Summary calculations
  const totalAssetValue = assets.reduce((sum, a) => sum + (a.acquisition_cost || 0), 0);
  const totalNBV = assets.reduce((sum, a) => sum + (a.net_book_value || 0), 0);
  const totalAccumulatedDepreciation = assets.reduce((sum, a) => sum + (a.accumulated_depreciation || 0), 0);
  const totalLoansOutstanding = loans.reduce((sum, l) => sum + (l.outstanding_principal || 0), 0);
  const totalMonthlyEMI = loans.reduce((sum, l) => sum + (l.emi_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="asset-management-title">
            Asset & Liability Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Fixed Assets, Depreciation & Loan Tracking
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-muted-foreground">Total Assets</span>
          </div>
          <p className="text-2xl font-bold">{assets.length}</p>
          <p className="text-xs text-muted-foreground">Registered assets</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            <span className="text-sm text-muted-foreground">Total Cost</span>
          </div>
          <p className="text-2xl font-bold">{totalAssetValue.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">SAR acquisition cost</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-muted-foreground">Net Book Value</span>
          </div>
          <p className="text-2xl font-bold">{totalNBV.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">After depreciation</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-muted-foreground">Loans Outstanding</span>
          </div>
          <p className="text-2xl font-bold">{totalLoansOutstanding.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Principal remaining</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-5 w-5 text-red-600" />
            <span className="text-sm text-muted-foreground">Monthly EMI</span>
          </div>
          <p className="text-2xl font-bold">{totalMonthlyEMI.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total monthly payment</p>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="fixed-assets" data-testid="fixed-assets-tab">Fixed Assets</TabsTrigger>
          <TabsTrigger value="depreciation" data-testid="depreciation-tab">Depreciation</TabsTrigger>
          <TabsTrigger value="loans" data-testid="loans-tab">Loans & EMI</TabsTrigger>
          <TabsTrigger value="bank-exposure" data-testid="bank-exposure-tab">Bank Exposure</TabsTrigger>
        </TabsList>

        {/* Fixed Assets Tab */}
        <TabsContent value="fixed-assets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Fixed Asset Register
            </h2>
            <Button onClick={() => setAssetDialogOpen(true)} data-testid="new-asset-btn">
              <Plus className="h-4 w-4 mr-2" />
              Register Asset
            </Button>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Asset Code</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Type</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Ownership</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Cost</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Accum. Depr.</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">NBV</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map(asset => (
                    <tr key={asset.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm">{asset.asset_code}</td>
                      <td className="px-4 py-3 text-sm font-medium">{asset.asset_name}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="text-xs">{asset.asset_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${ownershipColors[asset.ownership_type]}`}>
                          {asset.ownership_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{asset.acquisition_cost?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono text-amber-600">
                        ({asset.accumulated_depreciation?.toLocaleString()})
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">
                        {asset.net_book_value?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${assetStatusColors[asset.status]}`}>
                          {asset.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleViewAssetDepreciation(asset)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {assets.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">
                        <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No fixed assets registered yet</p>
                        <Button className="mt-4" onClick={() => setAssetDialogOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Register First Asset
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Depreciation Tab */}
        <TabsContent value="depreciation" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Depreciation Engine
            </h2>
          </div>

          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Period (YYYY-MM)</Label>
                <Input
                  type="month"
                  value={depreciationPeriod}
                  onChange={(e) => setDepreciationPeriod(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleCalculateDepreciation} data-testid="calculate-depr-btn">
                <Calculator className="h-4 w-4 mr-2" />
                Calculate
              </Button>
              {depreciationResult?.entries?.length > 0 && (
                <Button 
                  onClick={handlePostDepreciation} 
                  className="bg-green-600"
                  data-testid="post-depr-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Post to GL
                </Button>
              )}
            </div>
          </Card>

          {depreciationResult && (
            <Card>
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold">Depreciation for {depreciationResult.period}</h3>
                <p className="text-sm text-muted-foreground">
                  Total: SAR {depreciationResult.total_depreciation?.toLocaleString()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Asset</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Method</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">NBV Before</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Depreciation</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">NBV After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depreciationResult.entries?.map(entry => (
                      <tr key={entry.id} className="border-b">
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm mr-2">{entry.asset_code}</span>
                          {entry.asset_name}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className="text-xs">
                            {entry.depreciation_method?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{entry.nbv_before?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono text-red-600">
                          ({entry.depreciation_amount?.toLocaleString()})
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">
                          {entry.nbv_after?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {!depreciationResult && (
            <Card className="p-12 text-center">
              <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Calculate Monthly Depreciation</h3>
              <p className="text-muted-foreground">
                Select a period and click Calculate to generate depreciation entries for all active assets
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Loans Tab */}
        <TabsContent value="loans" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Loan & EMI Tracking
            </h2>
            <Button onClick={() => setLoanDialogOpen(true)} data-testid="new-loan-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Loan
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loans.map(loan => (
              <Card key={loan.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{loan.loan_name}</h3>
                    <p className="text-sm text-muted-foreground">{loan.bank_name}</p>
                  </div>
                  <Badge className={loanStatusColors[loan.status]}>{loan.status}</Badge>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Principal:</span>
                    <span className="font-mono">{loan.principal_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Outstanding:</span>
                    <span className="font-mono font-semibold text-red-600">
                      {loan.outstanding_principal?.toLocaleString()} SAR
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly EMI:</span>
                    <span className="font-mono">{loan.emi_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">EMIs Paid:</span>
                    <span className="font-mono">{loan.emis_paid} / {loan.tenure_months}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interest Rate:</span>
                    <span className="font-mono">{loan.interest_rate}% p.a.</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${(loan.emis_paid / loan.tenure_months) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {Math.round((loan.emis_paid / loan.tenure_months) * 100)}% complete
                  </p>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => handleViewLoanSchedule(loan)}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  View EMI Schedule
                </Button>
              </Card>
            ))}
            
            {loans.length === 0 && (
              <Card className="p-12 text-center col-span-full">
                <Landmark className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Loans Registered</h3>
                <p className="text-muted-foreground mb-4">
                  Track your asset financing and bank loans with EMI schedules
                </p>
                <Button onClick={() => setLoanDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Loan
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Bank Exposure Tab */}
        <TabsContent value="bank-exposure" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <PiggyBank className="h-5 w-5" />
              Bank Exposure Report
            </h2>
            <Button onClick={loadBankExposure} data-testid="load-exposure-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>

          {bankExposure ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Outstanding</p>
                  <p className="text-3xl font-bold">{bankExposure.total_outstanding?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">SAR</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Monthly EMI</p>
                  <p className="text-3xl font-bold">{bankExposure.total_monthly_emi?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">SAR / month</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Bank Partners</p>
                  <p className="text-3xl font-bold">{bankExposure.bank_count}</p>
                  <p className="text-xs text-muted-foreground">active banks</p>
                </Card>
              </div>

              {bankExposure.exposure_by_bank?.map(bank => (
                <Card key={bank.bank_name} className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Landmark className="h-5 w-5" />
                      {bank.bank_name}
                    </h3>
                    <Badge variant="outline">{bank.loan_count} loans</Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Principal</p>
                      <p className="text-lg font-semibold">{bank.total_principal?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Outstanding</p>
                      <p className="text-lg font-semibold text-red-600">{bank.outstanding_principal?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Interest Paid</p>
                      <p className="text-lg font-semibold">{bank.interest_paid?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly EMI</p>
                      <p className="text-lg font-semibold">{bank.monthly_emi?.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <p className="text-sm font-semibold mb-2">Active Loans:</p>
                    <div className="space-y-2">
                      {bank.loans?.map(loan => (
                        <div key={loan.loan_id} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                          <span>{loan.loan_name}</span>
                          <span className="font-mono">{loan.outstanding?.toLocaleString()} outstanding</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </>
          ) : (
            <Card className="p-12 text-center">
              <PiggyBank className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Bank Exposure Analysis</h3>
              <p className="text-muted-foreground mb-4">
                View consolidated loan exposure across all banking partners
              </p>
              <Button onClick={loadBankExposure}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* New Asset Dialog */}
      <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Register Fixed Asset
            </DialogTitle>
            <DialogDescription>
              Add a new fixed asset to the register
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Asset Name *</Label>
                <Input
                  value={newAsset.asset_name}
                  onChange={(e) => setNewAsset({...newAsset, asset_name: e.target.value})}
                  placeholder="e.g., Toyota Hilux 2024"
                />
              </div>
              <div>
                <Label>Asset Type</Label>
                <Select
                  value={newAsset.asset_type}
                  onValueChange={(v) => setNewAsset({...newAsset, asset_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="machinery">Machinery</SelectItem>
                    <SelectItem value="building">Building</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ownership Type</Label>
                <Select
                  value={newAsset.ownership_type}
                  onValueChange={(v) => setNewAsset({...newAsset, ownership_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owned">Owned</SelectItem>
                    <SelectItem value="financed">Financed</SelectItem>
                    <SelectItem value="leased">Leased</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Acquisition Date *</Label>
                <Input
                  type="date"
                  value={newAsset.acquisition_date}
                  onChange={(e) => setNewAsset({...newAsset, acquisition_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Acquisition Cost (SAR) *</Label>
                <Input
                  type="number"
                  value={newAsset.acquisition_cost || ''}
                  onChange={(e) => setNewAsset({...newAsset, acquisition_cost: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Residual Value (SAR)</Label>
                <Input
                  type="number"
                  value={newAsset.residual_value || ''}
                  onChange={(e) => setNewAsset({...newAsset, residual_value: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Useful Life (months)</Label>
                <Input
                  type="number"
                  value={newAsset.useful_life_months || ''}
                  onChange={(e) => setNewAsset({...newAsset, useful_life_months: parseInt(e.target.value) || 60})}
                />
              </div>
              <div>
                <Label>Depreciation Method</Label>
                <Select
                  value={newAsset.depreciation_method}
                  onValueChange={(v) => setNewAsset({...newAsset, depreciation_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="straight_line">Straight Line</SelectItem>
                    <SelectItem value="declining_balance">Declining Balance (WDV)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview calculation */}
            {newAsset.acquisition_cost > 0 && (
              <Card className="p-3 bg-slate-50">
                <p className="text-sm font-semibold mb-2">Depreciation Preview:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Depreciable Amount:</span>
                    <p className="font-mono font-semibold">
                      {(newAsset.acquisition_cost - newAsset.residual_value).toLocaleString()} SAR
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Monthly Depreciation:</span>
                    <p className="font-mono font-semibold">
                      {((newAsset.acquisition_cost - newAsset.residual_value) / newAsset.useful_life_months).toLocaleString(undefined, {maximumFractionDigits: 2})} SAR
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Annual Depreciation:</span>
                    <p className="font-mono font-semibold">
                      {(((newAsset.acquisition_cost - newAsset.residual_value) / newAsset.useful_life_months) * 12).toLocaleString(undefined, {maximumFractionDigits: 2})} SAR
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateAsset}
                disabled={!newAsset.asset_name || !newAsset.acquisition_cost}
                className="bg-accent"
              >
                Register Asset
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Loan Dialog */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Add New Loan
            </DialogTitle>
            <DialogDescription>
              Track a new loan with automatic EMI schedule generation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loan Name *</Label>
                <Input
                  value={newLoan.loan_name}
                  onChange={(e) => setNewLoan({...newLoan, loan_name: e.target.value})}
                  placeholder="e.g., Vehicle Loan - Hilux"
                />
              </div>
              <div>
                <Label>Loan Type</Label>
                <Select
                  value={newLoan.loan_type}
                  onValueChange={(v) => setNewLoan({...newLoan, loan_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vehicle">Vehicle Loan</SelectItem>
                    <SelectItem value="equipment">Equipment Loan</SelectItem>
                    <SelectItem value="working_capital">Working Capital</SelectItem>
                    <SelectItem value="term_loan">Term Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Bank Name *</Label>
              <Input
                value={newLoan.bank_name}
                onChange={(e) => setNewLoan({...newLoan, bank_name: e.target.value})}
                placeholder="e.g., Al Rajhi Bank"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Principal Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={newLoan.principal_amount || ''}
                  onChange={(e) => setNewLoan({...newLoan, principal_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Interest Rate (% p.a.) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newLoan.interest_rate || ''}
                  onChange={(e) => setNewLoan({...newLoan, interest_rate: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Tenure (months) *</Label>
                <Input
                  type="number"
                  value={newLoan.tenure_months || ''}
                  onChange={(e) => setNewLoan({...newLoan, tenure_months: parseInt(e.target.value) || 48})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>EMI Amount (SAR) *</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={newLoan.emi_amount || ''}
                    onChange={(e) => setNewLoan({...newLoan, emi_amount: parseFloat(e.target.value) || 0})}
                  />
                  <Button 
                    variant="outline" 
                    onClick={calculateEMI}
                    title="Calculate EMI"
                  >
                    <Calculator className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Disbursement Date *</Label>
                <Input
                  type="date"
                  value={newLoan.disbursement_date}
                  onChange={(e) => setNewLoan({...newLoan, disbursement_date: e.target.value})}
                />
              </div>
            </div>

            {/* Loan summary */}
            {newLoan.principal_amount > 0 && newLoan.emi_amount > 0 && (
              <Card className="p-3 bg-slate-50">
                <p className="text-sm font-semibold mb-2">Loan Summary:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Payable:</span>
                    <p className="font-mono font-semibold">
                      {(newLoan.emi_amount * newLoan.tenure_months).toLocaleString()} SAR
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Interest:</span>
                    <p className="font-mono font-semibold text-red-600">
                      {((newLoan.emi_amount * newLoan.tenure_months) - newLoan.principal_amount).toLocaleString()} SAR
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Interest Cost:</span>
                    <p className="font-mono font-semibold">
                      {(((newLoan.emi_amount * newLoan.tenure_months) - newLoan.principal_amount) / newLoan.principal_amount * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setLoanDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateLoan}
                disabled={!newLoan.loan_name || !newLoan.bank_name || !newLoan.principal_amount || !newLoan.emi_amount}
                className="bg-accent"
              >
                Create Loan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EMI Schedule Dialog */}
      <Dialog open={loanScheduleOpen} onOpenChange={setLoanScheduleOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              EMI Schedule - {selectedLoan?.loan_name}
            </DialogTitle>
            <DialogDescription>
              {selectedLoan?.bank_name} • {selectedLoan?.tenure_months} months • {selectedLoan?.interest_rate}% p.a.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-semibold">#</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Date</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">EMI</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Principal</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Interest</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Balance</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Status</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loanSchedule.map(emi => (
                    <tr key={emi.id} className={`border-b ${emi.status === 'paid' ? 'bg-green-50' : ''}`}>
                      <td className="px-3 py-2 text-sm">{emi.emi_number}</td>
                      <td className="px-3 py-2 text-sm">{emi.emi_date}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm">{emi.emi_amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm">{emi.principal_component?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-amber-600">
                        {emi.interest_component?.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm font-semibold">
                        {emi.closing_balance?.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={emi.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {emi.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {emi.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRecordPayment(selectedLoan.id, emi.emi_number)}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Pay
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Detail / Depreciation Schedule Dialog */}
      <Dialog open={assetDetailOpen} onOpenChange={setAssetDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Depreciation Schedule - {selectedAsset?.asset_name}
            </DialogTitle>
            <DialogDescription>
              {selectedAsset?.asset_code} • {assetDepreciationSchedule?.useful_life_months} months useful life
            </DialogDescription>
          </DialogHeader>

          {assetDepreciationSchedule && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Acquisition Cost</p>
                  <p className="text-lg font-bold">{assetDepreciationSchedule.acquisition_cost?.toLocaleString()}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Residual Value</p>
                  <p className="text-lg font-bold">{assetDepreciationSchedule.residual_value?.toLocaleString()}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Monthly Depr.</p>
                  <p className="text-lg font-bold">{assetDepreciationSchedule.monthly_depreciation?.toLocaleString()}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Useful Life</p>
                  <p className="text-lg font-bold">{assetDepreciationSchedule.useful_life_months} months</p>
                </Card>
              </div>

              <div className="overflow-x-auto max-h-[50vh]">
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-sm font-semibold">Month</th>
                      <th className="px-3 py-2 text-left text-sm font-semibold">Period</th>
                      <th className="px-3 py-2 text-right text-sm font-semibold">Depreciation</th>
                      <th className="px-3 py-2 text-right text-sm font-semibold">Accumulated</th>
                      <th className="px-3 py-2 text-right text-sm font-semibold">NBV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetDepreciationSchedule.schedule?.map(row => (
                      <tr key={row.month} className="border-b">
                        <td className="px-3 py-2 text-sm">{row.month}</td>
                        <td className="px-3 py-2 text-sm">{row.period}</td>
                        <td className="px-3 py-2 text-right font-mono text-sm text-red-600">
                          ({row.depreciation?.toLocaleString()})
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm">
                          {row.accumulated?.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm font-semibold">
                          {row.nbv?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
